import nstr from "nstr";

import type { Deployment, UserProfile, Workspace } from "@/supabase.types";

export function isDefined<T>(value: T | undefined | null): value is T {
  return (value as T) !== undefined && (value as T) !== null;
}

export function isDefinedString<T extends string>(value: T | undefined | null): value is T {
  return value !== undefined && value !== null && value !== "";
}

export function isDefinedNumber<T extends number>(value: T | undefined | null): value is T {
  return value !== undefined && value !== null && value !== 0;
}

export const userName = (user?: Partial<UserProfile> | null) => {
  if (!user) return "";
  if (!user.first_name && !user.last_name) return user.email;
  return `${isDefinedString(user.first_name) ? user.first_name : ""} ${isDefinedString(user.last_name) ? user.last_name : ""}`.trim();
};

export const deploymentName = (deployment?: Partial<Deployment> | null, workspace?: Partial<Workspace> | null) => {
  let ws_name = "";
  if (isDefinedString(workspace?.name)) {
    ws_name = `(${workspace.name})`;
  }
  let depl_name = "";
  if (isDefinedString(deployment?.name)) {
    depl_name = deployment.name;
    if (ws_name) {
      depl_name += ` ${ws_name}`;
    }
  }
  return depl_name;
};

export const addressName = ({
  address_1,
  address_2,
  city,
  zipcode,
  country,
}: Pick<Partial<Deployment | Workspace>, "address_1" | "address_2" | "city" | "zipcode" | "country">) => {
  return [address_1, address_2, city, zipcode, country].filter((v) => !!v).join(", ");
};

export function byKey<T extends Record<string, unknown>, K extends T[keyof T] & string>(
  collection: T[],
  key: keyof T,
): Partial<Record<K, T>> {
  return collection.reduce(
    (reduceObj, item) => {
      reduceObj[item[key] as K] = item;
      return reduceObj;
    },
    {} as Record<K, T>,
  );
}

export const groupBy = <T extends Record<string, unknown>>(
  arr: T[],
  key: keyof T,
  initial = {} as Record<string, T[]>,
): Record<string, T[]> => {
  if (arr.length <= 0) return initial;
  return arr.reduce((reduceObj, item) => {
    (reduceObj[item[key] as string] = reduceObj[item[key] as string] ?? []).push(item);
    return reduceObj;
  }, initial);
};

export const groupByProcessed = <T extends Record<string, unknown>>(
  arr: T[],
  key: keyof T,
  process: (key: keyof T) => string,
  initial = {} as Record<string, T[]>,
): Record<string, T[]> => {
  if (arr.length <= 0) return initial;
  return arr.reduce((reduceObj, item) => {
    (reduceObj[process(item[key] as string)] = reduceObj[process(item[key] as string)] ?? []).push(item);
    return reduceObj;
  }, initial);
};

export const deduplicateArrayBy = <T>(arr: T[], key: keyof T): T[] => {
  return Array.from(new Map(arr.map((item) => [item[key], item])).values());
};

export const partitionArrayByFilter = <T>(array: T[], filter: (item: T, index: number, array: T[]) => boolean) => {
  const pass: T[] = [],
    fail: T[] = [];
  array.forEach((item, index, _array) => (filter(item, index, _array) ? pass : fail).push(item));
  return [pass, fail];
};

export const getAddedRemovedItems = <T>(oldArray: T[], newArray: T[]) => {
  const added = newArray.filter((item) => !oldArray.includes(item));
  const removed = oldArray.filter((item) => !newArray.includes(item));
  return {
    added,
    removed,
  };
};

export function removeTemplateLiteralLineBreaks(str: string) {
  return str.replace(/\n/g, "");
}

// source: https://stackoverflow.com/questions/43118692/typescript-filter-out-nulls-from-an-array
export function filterNotEmpty<TValue>(value: TValue | null | undefined): value is TValue {
  if (value === null || value === undefined) return false;
  return true;
}

export function filterEmptyObjectFields<T extends Record<string, unknown>>(object: T): T {
  const _object = Object.assign({}, object);
  Object.keys(_object).forEach((key) => {
    if (_object[key] === undefined) {
      delete _object[key];
    }
  });
  return _object;
}

export const getFirstArrayValue = <T>(array?: T[] | null): T | undefined => {
  if (!array) return undefined;
  if (array.length <= 0) return undefined;
  return array[0];
};

export const formatNumberDecimals = (value?: number | null, decimals = 0) => {
  if (!isDefined(value)) return null;
  const factor = Math.pow(10, decimals);
  const preresult = value * factor * (1 + Number.EPSILON);
  return Math.round(preresult) / factor;
};

export function stringifyObjectValues(myObj: Record<string, unknown>): Record<string, string> {
  const stringifiedObj = Object.assign({}, myObj);
  Object.keys(myObj).forEach((key) => {
    if (typeof stringifiedObj[key] === "object") {
      stringifiedObj[key] = JSON.stringify(stringifiedObj[key]);
    } else {
      // eslint-disable-next-line @typescript-eslint/no-base-to-string
      stringifiedObj[key] = String(stringifiedObj[key]);
    }
  });
  return stringifiedObj as Record<string, string>;
}

export const getSafePercentage = (a?: number, b?: number) => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return (a === 0 || !a) && (b === 0 || !b) ? 0 : Math.round((a! / b!) * 100);
};

/**
 * @param precision Decimal places to round for (1,2,3...)
 */
export const getRoundedToPrecision = (value: number, precision?: number) => {
  if (!precision) return value;
  const base = Math.pow(10, precision);
  // Number.EPSILON, see: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Number/EPSILON
  return Math.round((value + Number.EPSILON) * base) / base;
};

export const moveArrayItem = <T>(items: T[], oldIndex: number, newIndex: number) => {
  const temp_items: (T | undefined)[] = [...items];
  if (newIndex >= temp_items.length) {
    let k = newIndex - temp_items.length + 1;
    while (k--) {
      temp_items.push(undefined);
    }
  }
  temp_items.splice(newIndex, 0, temp_items.splice(oldIndex, 1)[0]);
  return temp_items.filter(filterNotEmpty); // for testing
};

export const safeFormatNumber = (value?: number | null, precision = 2): string | null => {
  if (!value) return null;
  return nstr(value, { maxDecimals: precision });
};

export const safeFormatAddress = <
  T extends {
    address_1?: string | null;
    address_2?: string | null;
    city?: string | null;
    country?: string | null;
    zipcode?: string | null;
  },
>(
  values: T,
): T & {
  address_1: string;
  address_2: string;
  city: string;
  country: string;
  zipcode: string;
} => {
  return {
    ...values,
    address_1: values.address_1 ? values.address_1 : "",
    address_2: values.address_2 ? values.address_2 : "",
    city: values.city ? values.city : "",
    country: values.country ? values.country : "",
    zipcode: values.zipcode ? values.zipcode : "",
  };
};

type FilterUndefined<T> = T extends undefined ? never : T;
type FilterNull<T> = T extends null ? never : T;
type FilterUndefinedAndNull<T> = FilterUndefined<FilterNull<T>>;

type ExtractFromObject<O extends Record<PropertyKey, unknown>, K> = K extends keyof O
  ? O[K]
  : K extends keyof FilterUndefinedAndNull<O>
    ? FilterUndefinedAndNull<O>[K] | undefined
    : undefined;

type ExtractFromArray<A extends readonly unknown[], K> = unknown[] extends A
  ? A extends readonly (infer T)[]
    ? T | undefined
    : undefined
  : K extends keyof A
    ? A[K]
    : undefined;

type GetWithArray<O, K> = K extends []
  ? O
  : K extends [infer Key, ...infer Rest]
    ? O extends Record<PropertyKey, unknown>
      ? GetWithArray<ExtractFromObject<O, Key>, Rest>
      : O extends readonly unknown[]
        ? GetWithArray<ExtractFromArray<O, Key>, Rest>
        : undefined
    : never;

type Path<T> = T extends `${infer Key}.${infer Rest}` ? [Key, ...Path<Rest>] : T extends infer Key ? [Key] : [];

type Get<O, K> = GetWithArray<O, Path<K>>;

export function objectValueByPath<O, K extends string>(obj: O, path: K): Get<O, K>;
export function objectValueByPath(obj: Record<string, unknown>, path: string): unknown {
  const keys = path.split(".");

  let currentObj = obj;
  for (const key of keys) {
    const value = currentObj[key];
    if (value === undefined || value === null) {
      return undefined;
    }

    currentObj = value as Record<string, unknown>;
  }

  return currentObj;
}

export const trimFormDataStrings = <T>(data: T): T => {
  // Handle primitives
  if (data === null || typeof data !== "object") {
    return typeof data === "string" ? (data.trim() as T) : data;
  }

  // Handle Date, RegExp, and other special objects
  if (
    data instanceof Date ||
    data instanceof RegExp ||
    data instanceof Map ||
    data instanceof Set ||
    data instanceof WeakMap ||
    data instanceof WeakSet ||
    data instanceof File ||
    data instanceof Blob
  ) {
    return data;
  }

  // Handle arrays
  if (Array.isArray(data)) {
    return data.map(trimFormDataStrings) as unknown as T;
  }

  // Handle plain objects
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    result[key] = trimFormDataStrings(value);
  }

  return result as T;
};

export const sanitizeFormData = <T>(data: T): T => {
  return trimFormDataStrings(data);
};

type CompareWithinRangeOperator = "lt" | "lte" | "gt" | "gte" | "eq";

export const compareWithinRange = (
  a: number,
  b: number,
  op: CompareWithinRangeOperator,
  offsetPercent = 0,
): boolean => {
  const tolerance = Math.abs(b) * (offsetPercent / 100);
  switch (op) {
    case "lt":
      return a < b + tolerance;
    case "lte":
      return a <= b + tolerance;
    case "gt":
      return a > b - tolerance;
    case "gte":
      return a >= b - tolerance;
    case "eq":
      return Math.abs(a - b) <= tolerance;
  }
};

// backend multi-value filters are non-empty arrays or absent: an empty selection means "no filter"
export const toNonEmptyArray = <T>(values: T[]): [T, ...T[]] | undefined =>
  values.length > 0 ? (values as [T, ...T[]]) : undefined;
